import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { auth } from '../lib/firebase';
import {
  getChatContacts,
  getAllConversations,
  openConversation as openConversationRequest,
  getMessages,
  sendChatMessage,
  sendChatAttachment as sendChatAttachmentRequest,
  markConversationRead,
  type ChatContactDto,
  type ConversationDto,
  type MessageDto,
} from '../lib/api/endpoints';
import { subscribeConversationsLive, subscribeMessagesLive } from '../lib/firestore/chatLive';

// FE-21 (ADR-026): antes de este archivo, el chat era un `useState([])` — lo
// que escribías moría con la pestaña. Ahora se envía por REST y se lee por
// Firestore, pero Firestore aquí es SOLO la señal de "algo cambió"
// (`chatLive.ts` documenta por qué: el doc proyectado no trae ni
// `senderDisplayName` ni la ficha del adjunto). Cada snapshot dispara un
// refetch por REST, que es quien conserva la forma completa — mismo patrón
// que `upsertAlert`/`vehiclesLive.ts`, aplicado aquí en vez de esperar a que
// #113 (Frontend_Rulebook.md §"Estado del servidor") lo resuelva para toda
// la app.
//
// 🚨 Dos espacios de identidad distintos y no intercambiables:
// `user.id` (`app_user.id`, UUID de Postgres) es lo que trae el DTO de REST
// en `senderUserId`/`participants[].userId` — con eso se compara "es mío" o
// "participo". `auth.currentUser.uid` es el uid de Identity Platform que
// exige `participantUids` en las reglas de Firestore — con eso se arma el
// filtro de la suscripción. Nunca se cruzan.

const MESSAGES_PAGE_SIZE = 50;

function sortBySentAt(messages: MessageDto[]): MessageDto[] {
  return [...messages].sort((a, b) => a.sentAt.localeCompare(b.sentAt));
}

function mergeById(base: MessageDto[], incoming: MessageDto[]): MessageDto[] {
  const byId = new Map(base.map(m => [m.id, m]));
  for (const m of incoming) byId.set(m.id, m);
  return sortBySentAt([...byId.values()]);
}

interface ChatContextValue {
  contacts: ChatContactDto[];
  conversations: ConversationDto[];
  isLoading: boolean;
  error: Error | null;
  /** Suma de `unreadCount` de todas las conversaciones — el badge del FAB. */
  unreadCount: number;

  activeConversationId: string | null;
  activeMessages: MessageDto[];
  isLoadingMessages: boolean;
  hasMoreMessages: boolean;
  /**
   * false cuando el Administrador está leyendo una conversación ajena (lee
   * todo el tenant, pero `SendMessage` le exige participación — `403`
   * medido en `EP-55-chat-persistido.md` §3.5). El resto de roles solo ve
   * conversaciones propias, así que siempre es `true` para ellos.
   */
  canSendInActive: boolean;

  /** Abre (o recupera, si `contact.conversationId` ya la trae) la conversación con este contacto y la activa. */
  selectContact: (contact: ChatContactDto) => Promise<void>;
  selectConversation: (conversationId: string) => void;
  loadOlderMessages: () => Promise<void>;
  sendText: (body: string) => Promise<void>;
  sendAttachment: (file: File, body: string | undefined, onProgress?: (fraction: number) => void) => Promise<void>;
  markActiveRead: () => void;
}

const ChatContext = createContext<ChatContextValue | null>(null);

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  // Mismo criterio que AlertsContext (issue #102): la carga se ata a quién
  // está en sesión, no al montaje del provider.
  const sessionKey = user ? `${user.tenantId}:${user.id}` : null;

  const [contacts, setContacts] = useState<ChatContactDto[]>([]);
  const [conversations, setConversations] = useState<ConversationDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [messagesByConversation, setMessagesByConversation] = useState<Record<string, MessageDto[]>>({});
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [hasMoreByConversation, setHasMoreByConversation] = useState<Record<string, boolean>>({});
  const pageByConversation = useRef<Record<string, number>>({});

  // --- Contactos (matriz por rol, ya resuelta por el backend) + conversaciones ---
  useEffect(() => {
    if (!sessionKey || !user) {
      setContacts([]);
      setConversations([]);
      setActiveConversationId(null);
      setMessagesByConversation({});
      setError(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const loadConversations = async () => {
      const items = await getAllConversations();
      if (!cancelled) setConversations(items);
    };

    const load = async () => {
      try {
        const [contactsRes] = await Promise.all([getChatContacts(), loadConversations()]);
        if (cancelled) return;
        setContacts(contactsRes.items);
        setError(null);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err : new Error('Error desconocido cargando el chat'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();

    // El Administrador se suscribe SIN filtro (no está en `participantUids`);
    // el resto exige `array-contains` con su uid de Identity Platform. Un
    // snapshot no trae dato para pintar — solo dispara `loadConversations()`.
    const firebaseUid = auth.currentUser?.uid;
    const unsubscribe = firebaseUid
      ? subscribeConversationsLive(
          user.tenantId,
          firebaseUid,
          isAdmin,
          () => { if (!cancelled) loadConversations().catch(err => console.error('No se pudo refrescar conversaciones', err)); },
          err => console.error('Suscripción a conversaciones en vivo caída', err),
        )
      : undefined;

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [sessionKey, user, isAdmin]);

  // --- Mensajes de la conversación activa ---
  useEffect(() => {
    if (!activeConversationId || !user) return;
    const conversationId = activeConversationId;
    let cancelled = false;

    const loadFirstPage = async () => {
      setIsLoadingMessages(true);
      try {
        const res = await getMessages(conversationId, 1, MESSAGES_PAGE_SIZE);
        if (cancelled) return;
        pageByConversation.current[conversationId] = 1;
        setMessagesByConversation(prev => ({ ...prev, [conversationId]: sortBySentAt(res.items) }));
        setHasMoreByConversation(prev => ({ ...prev, [conversationId]: res.items.length === MESSAGES_PAGE_SIZE }));
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err : new Error('Error cargando los mensajes'));
      } finally {
        if (!cancelled) setIsLoadingMessages(false);
      }
    };
    loadFirstPage();

    // Igual que con las conversaciones: el snapshot de mensajes solo avisa,
    // el contenido real (adjunto completo, `senderDisplayName`) vuelve a
    // pedirse por REST.
    const refetchLatest = async () => {
      try {
        const res = await getMessages(conversationId, 1, MESSAGES_PAGE_SIZE);
        if (cancelled) return;
        setMessagesByConversation(prev => ({ ...prev, [conversationId]: mergeById(prev[conversationId] ?? [], res.items) }));
      } catch (err) {
        console.error('No se pudo refrescar los mensajes tras la señal en vivo', err);
      }
    };

    const firebaseUid = auth.currentUser?.uid;
    const unsubscribe = firebaseUid
      ? subscribeMessagesLive(
          user.tenantId,
          conversationId,
          firebaseUid,
          isAdmin,
          () => { if (!cancelled) refetchLatest(); },
          err => console.error('Suscripción a mensajes en vivo caída', err),
        )
      : undefined;

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [activeConversationId, user, isAdmin]);

  const selectConversation = useCallback((conversationId: string) => {
    setActiveConversationId(conversationId);
  }, []);

  // Idempotente del lado del backend (POST /conversations devuelve la que ya
  // existía en vez de duplicarla) — aquí solo se evita la llamada de más
  // cuando el contacto ya trae `conversationId`.
  const selectContact = useCallback(async (contact: ChatContactDto) => {
    if (contact.conversationId) {
      selectConversation(contact.conversationId);
      return;
    }
    const conversation = await openConversationRequest(contact.userId);
    setConversations(prev => (prev.some(c => c.id === conversation.id) ? prev : [conversation, ...prev]));
    setContacts(prev => prev.map(c => (c.userId === contact.userId ? { ...c, conversationId: conversation.id } : c)));
    selectConversation(conversation.id);
  }, [selectConversation]);

  const loadOlderMessages = useCallback(async () => {
    const conversationId = activeConversationId;
    if (!conversationId || !hasMoreByConversation[conversationId]) return;
    const nextPage = (pageByConversation.current[conversationId] ?? 1) + 1;
    const res = await getMessages(conversationId, nextPage, MESSAGES_PAGE_SIZE);
    pageByConversation.current[conversationId] = nextPage;
    setMessagesByConversation(prev => ({ ...prev, [conversationId]: mergeById(prev[conversationId] ?? [], res.items) }));
    setHasMoreByConversation(prev => ({ ...prev, [conversationId]: res.items.length === MESSAGES_PAGE_SIZE }));
  }, [activeConversationId, hasMoreByConversation]);

  const upsertMessageLocal = useCallback((conversationId: string, message: MessageDto, replaceTempId?: string) => {
    setMessagesByConversation(prev => {
      const existing = prev[conversationId] ?? [];
      const withoutTemp = replaceTempId ? existing.filter(m => m.id !== replaceTempId) : existing;
      return { ...prev, [conversationId]: mergeById(withoutTemp, [message]) };
    });
    setConversations(prev => prev.map(c => (c.id === conversationId ? { ...c, lastMessage: message } : c)));
  }, []);

  // Mensaje optimista local — nunca una escritura directa a Firestore (las
  // reglas la niegan). Si el POST falla, se retira: no hay todavía un
  // sistema de toasts, así que el error se propaga para que quien llamó
  // decida qué hacer con el texto que no se pudo mandar.
  const sendText = useCallback(async (body: string) => {
    if (!activeConversationId || !user) return;
    const conversationId = activeConversationId;
    const tempId = `temp-${Date.now()}`;
    const optimistic: MessageDto = {
      id: tempId,
      conversationId,
      senderUserId: user.id,
      senderDisplayName: user.name,
      body,
      sentAt: new Date().toISOString(),
    };
    setMessagesByConversation(prev => ({ ...prev, [conversationId]: [...(prev[conversationId] ?? []), optimistic] }));

    try {
      const message = await sendChatMessage(conversationId, body);
      upsertMessageLocal(conversationId, message, tempId);
    } catch (err) {
      setMessagesByConversation(prev => ({ ...prev, [conversationId]: (prev[conversationId] ?? []).filter(m => m.id !== tempId) }));
      throw err;
    }
  }, [activeConversationId, user, upsertMessageLocal]);

  // Sin bubble optimista: hasta que el `POST` no resuelve no hay adjunto real
  // que mostrar. `onProgress` es lo que evita que una subida de 50 MiB se
  // vea como colgada.
  const sendAttachment = useCallback(async (file: File, body: string | undefined, onProgress?: (fraction: number) => void) => {
    if (!activeConversationId) return;
    const message = await sendChatAttachmentRequest(activeConversationId, file, body, onProgress);
    upsertMessageLocal(activeConversationId, message);
  }, [activeConversationId, upsertMessageLocal]);

  // Optimista: el contador baja de inmediato, y el `POST` es idempotente
  // (repetirlo deja lo mismo) así que no hay nada que revertir si falla —
  // solo queda registrado en consola.
  const markActiveRead = useCallback(() => {
    const conversationId = activeConversationId;
    if (!conversationId) return;
    setConversations(prev => prev.map(c => (c.id === conversationId ? { ...c, unreadCount: 0 } : c)));
    markConversationRead(conversationId).catch(err => {
      console.error('No se pudo marcar la conversación como leída en el backend', err);
    });
  }, [activeConversationId]);

  const activeConversation = conversations.find(c => c.id === activeConversationId) ?? null;
  const canSendInActive = !activeConversation
    ? false
    : !isAdmin || activeConversation.participants.some(p => p.userId === user?.id);

  const unreadCount = conversations.reduce((sum, c) => sum + c.unreadCount, 0);

  return (
    <ChatContext.Provider
      value={{
        contacts,
        conversations,
        isLoading,
        error,
        unreadCount,
        activeConversationId,
        activeMessages: activeConversationId ? (messagesByConversation[activeConversationId] ?? []) : [],
        isLoadingMessages,
        hasMoreMessages: activeConversationId ? (hasMoreByConversation[activeConversationId] ?? false) : false,
        canSendInActive,
        selectContact,
        selectConversation,
        loadOlderMessages,
        sendText,
        sendAttachment,
        markActiveRead,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export function useChat(): ChatContextValue {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used within ChatProvider');
  return ctx;
}
